import React, { useState, useRef } from 'react';
import cx from 'classnames';

import {
    Button,
    Checkbox,
    DropdownWrapper,
    DropdownOption,
    IconFile,
    IconInfoCircle,
} from '@trqk/components';
import {
    constants,
    getRegionFilterLabel,
    getStatementLabel,
    locale,
} from '@trqk/utils';

import { expandedHeights, height, presetClasses, width } from './constants';
import { getScrollAreaHeight } from './helpers';
import { ManageStatementsButton } from '../../nav';

const { REGION_LABEL_MAP } = constants;

interface StatementSelectorProps {
    statementSelectionProps: StatementSelectionProps;
}

/**
 * Provides the ability to select one or more statements for viewing, based on the current user and
 * currently selected entity. Statement selection is available through several preset values,
 * including "All Statements" and "Year to Date". Statements can also be selected one by one.
 * @param Props props sourced from the useStatementSelection hook
 */
export const StatementSelector = ({
    statementSelectionProps,
}: StatementSelectorProps): JSX.Element => {
    const {
        currentSelectionSet,
        editing,
        isExpanded,
        saved,
        selectionId,
        sendStatementSelectionEvent,
        setIsExpanded,
        ready,
    } = statementSelectionProps;
    const [regionFilterIsExpanded, setRegionFilterIsExpanded] = useState(false);
    const activeRegionFilter = editing
        ? editing.regionFilter
        : saved.regionFilter;
    const displayRegionFilter = saved.regionFilter !== null;

    // activeSelectionSet is the selectionSet that is currently being edited. It is located in the
    // StatementSelectionMachine on the `editing` property under the currently selected regionFilter.
    const activeSelectionSet =
        editing?.selectionSets?.[editing.regionFilter] ??
        saved.selectionSets[saved.regionFilter];

    let nonScrollAreaHeight =
        expandedHeights.allStatements +
        expandedHeights.yearToDate +
        expandedHeights.viewSelected +
        expandedHeights.clearAll +
        expandedHeights.manageOrAddStatements +
        expandedHeights.statementPeriodInfo;

    if (displayRegionFilter) {
        nonScrollAreaHeight += expandedHeights.regionFilter;
    }
    // Calculate heights for rendering dropdown components
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollAreaHeight = getScrollAreaHeight(
        containerRef,
        nonScrollAreaHeight,
    );
    const listHeight = scrollAreaHeight + nonScrollAreaHeight;

    const disableYearToDate =
        activeSelectionSet.presets.yearToDate.ids.length < 1;
    const disableAll = activeSelectionSet.presets.all.ids.length < 1;

    const regionFilterOptions = Array.from(REGION_LABEL_MAP.keys()).reduce(
        (
            acc: DropdownOption<PRORegionFilter>[],
            key: string,
        ): DropdownOption<PRORegionFilter>[] => {
            // Do not add an "International" dropdown option unless there is at least one
            // selectable international statementView
            if (key === 'international') {
                if (
                    saved.selectionSets.international.selectableViews.length > 0
                ) {
                    acc.push({
                        label: REGION_LABEL_MAP.get(key),
                        value: key as PRORegionFilter,
                    });
                }
            } else {
                acc.push({
                    label: REGION_LABEL_MAP.get(key),
                    value: key as PRORegionFilter,
                });
            }

            return acc;
        },
        [] as DropdownOption<PRORegionFilter>[],
    );

    return (
        <div ref={containerRef}>
            <DropdownWrapper
                boxShadowWhenClosed={true}
                boxShadowWhenOpen={true}
                disabled={!ready}
                size="medium"
                isExpanded={isExpanded}
                setIsExpanded={(expanded: boolean): void => {
                    setIsExpanded(expanded);

                    if (expanded) {
                        sendStatementSelectionEvent({
                            type: 'START_EDITING',
                            value: {
                                id: selectionId,
                            },
                        });
                    }
                }}
                width={width}
                height={height}
                listHeight={listHeight}
                selectedLabel={
                    <div className="flex items-center">
                        <IconFile
                            fillColor="secondary-500"
                            addClasses="mr-2"
                            height={20}
                        />
                        {currentSelectionSet.label.long}
                    </div>
                }
                closeOnClickOutside={true}
                handleCloseOnClickOutside={(): void => {
                    sendStatementSelectionEvent({
                        type: 'CANCEL_EDITING',
                        value: {
                            id: selectionId,
                        },
                    });
                }}
            >
                <div
                    className="pt-4 px-4 pb-2 grid"
                    style={{
                        gridTemplateColumns: '22px auto',
                        height: expandedHeights.statementPeriodInfo,
                    }}
                >
                    <IconInfoCircle width={16} fillColor="tertiary-400" />
                    <p className="text-xs text-tertiary-400">
                        {
                            locale.enUS.STATEMENT_SELECTOR
                                .LABELED_BY_DISTRIBUTION_PERIOD
                        }
                    </p>
                </div>
                {displayRegionFilter && (
                    <div className="flex justify-center py-2">
                        <DropdownWrapper
                            disabled={regionFilterOptions.length < 2}
                            boxShadowWhenOpen={false}
                            size="medium"
                            isExpanded={regionFilterIsExpanded}
                            setIsExpanded={setRegionFilterIsExpanded}
                            height={expandedHeights.regionFilterDropdown}
                            width={width - 20}
                            description="Type:"
                            selectedLabel={
                                <div className="text-primary-500">
                                    {getRegionFilterLabel(activeRegionFilter)}
                                </div>
                            }
                            borderConfig="solid.medium"
                        >
                            {regionFilterOptions.map(
                                (
                                    option: DropdownOption<PRORegionFilter>,
                                    index: number,
                                ): JSX.Element => (
                                    <DropdownOption
                                        key={option.value ?? `key-${index}`}
                                        option={option}
                                        height={
                                            expandedHeights.regionFilterDropdown
                                        }
                                        handleChange={(
                                            currentOption: DropdownOption<PRORegionFilter>,
                                        ): void => {
                                            sendStatementSelectionEvent({
                                                type: 'SET_REGION_FILTER',
                                                value: {
                                                    id: selectionId,
                                                    regionFilter:
                                                        currentOption.value,
                                                },
                                            });
                                            setRegionFilterIsExpanded(false);
                                        }}
                                    />
                                ),
                            )}
                        </DropdownWrapper>
                    </div>
                )}
                {/* "All" preset */}
                <DropdownOption
                    disabled={disableAll}
                    handleChange={(): void => {
                        setIsExpanded(false);
                        sendStatementSelectionEvent({
                            type: 'SET_SELECTED_BY_PRESET_BUTTON',
                            value: {
                                id: selectionId,
                                activeRegionFilter,
                                selectionPreset: 'all',
                            },
                        });
                    }}
                    height={expandedHeights.allStatements}
                    option={{
                        label: (
                            <div
                                className={cx(presetClasses, {
                                    'hover:bg-highlight-fadedpurple':
                                        !disableAll,
                                    'text-tertiary-300': disableAll,
                                    'text-tertiary-500': !disableAll,
                                })}
                                style={{
                                    height: expandedHeights.allStatements,
                                }}
                            >
                                <div>
                                    <div>{locale.enUS.ALL_STATEMENTS}</div>
                                    {activeSelectionSet.presets.all.label
                                        .long !==
                                        locale.enUS.ALL_STATEMENTS && (
                                        <div className="text-tertiary-300 text-xs">
                                            {`${activeSelectionSet.presets.all.label.long}`}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ),
                        value: activeSelectionSet.presets.all.ids,
                    }}
                    leftPadding={false}
                    underlineOnHover={false}
                />
                {/* Clear All */}
                <DropdownOption
                    handleChange={(): void => {
                        sendStatementSelectionEvent({
                            type: 'SET_SELECTED',
                            value: {
                                id: selectionId,
                                selectedStatementIds: [],
                            },
                        });
                    }}
                    height={expandedHeights.yearToDate}
                    option={{
                        label: (
                            <div
                                className={cx(
                                    presetClasses,
                                    'hover:bg-highlight-fadedpurple',
                                )}
                                style={{
                                    height: expandedHeights.clearAll,
                                }}
                            >
                                {locale.enUS.STATEMENT_SELECTOR.CLEAR_ALL}
                            </div>
                        ),
                        value: [],
                    }}
                    leftPadding={false}
                    underlineOnHover={false}
                />
                {/* "Year to Date" preset */}
                <DropdownOption
                    disabled={disableYearToDate}
                    handleChange={(): void => {
                        setIsExpanded(false);
                        sendStatementSelectionEvent({
                            type: 'SET_SELECTED_BY_PRESET_BUTTON',
                            value: {
                                id: selectionId,
                                activeRegionFilter,
                                selectionPreset: 'yearToDate',
                            },
                        });
                    }}
                    height={expandedHeights.yearToDate}
                    option={{
                        label: (
                            <div
                                className={cx(presetClasses, {
                                    'hover:bg-highlight-fadedpurple':
                                        !disableYearToDate,
                                    'text-tertiary-300': disableYearToDate,
                                    'text-tertiary-500': !disableYearToDate,
                                })}
                                style={{
                                    height: expandedHeights.yearToDate,
                                }}
                            >
                                <div>
                                    <div>
                                        {locale.enUS.YEAR_TO_DATE_STATEMENTS}
                                    </div>
                                    {activeSelectionSet.presets.yearToDate.label
                                        .long !==
                                        locale.enUS.YEAR_TO_DATE_STATEMENTS && (
                                        <div className="text-tertiary-300 text-xs">
                                            {`${activeSelectionSet.presets.yearToDate.label.long}`}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ),
                        value: activeSelectionSet.presets.yearToDate.ids,
                    }}
                    leftPadding={false}
                    underlineOnHover={false}
                />
                <div
                    className="overflow-y-auto"
                    style={{ maxHeight: scrollAreaHeight }}
                >
                    {/* List of selectable views for the current regionFilter - for selecting one by one */}
                    {activeSelectionSet.selectableViews.map(
                        (
                            statementView: TrqkStatementView,
                            index: number,
                        ): JSX.Element => {
                            const { statement_id } = statementView;
                            const statementLabel =
                                getStatementLabel(statementView);

                            const checked =
                                statement_id !== undefined &&
                                statement_id !== null &&
                                activeSelectionSet.selectedIds.includes(
                                    statement_id,
                                );

                            return (
                                <div
                                    key={
                                        statement_id !== null
                                            ? statement_id
                                            : `statement-${index}`
                                    }
                                    style={{
                                        height: expandedHeights.scrollAreaItem,
                                    }}
                                    className="pl-4 pt-1 flex items-center"
                                >
                                    <Checkbox
                                        inputId={`statement-id-${statement_id}`}
                                        checked={checked}
                                        onChange={(): void => {
                                            if (statement_id) {
                                                sendStatementSelectionEvent({
                                                    type: 'TOGGLE_SELECTED',
                                                    value: {
                                                        id: selectionId,
                                                        statementId:
                                                            statement_id,
                                                        selected: !checked,
                                                    },
                                                });
                                            }
                                        }}
                                        label={statementLabel}
                                        labelClasses="font-medium text-sm text-tertiary-500"
                                        size="large"
                                        color="primary"
                                    />
                                </div>
                            );
                        },
                    )}
                </div>
                <div
                    className="flex items-center justify-center p-1"
                    style={{ height: expandedHeights.viewSelected }}
                >
                    <Button
                        disabled={
                            (activeSelectionSet.selectedIds?.length ?? 0) < 1
                        }
                        color="primary"
                        handleClick={(): void => {
                            setIsExpanded(false);
                            sendStatementSelectionEvent({
                                type: 'SAVE_EDITS',
                                value: {
                                    id: selectionId,
                                },
                            });
                        }}
                        addClasses="text-center"
                    >
                        {`View Selected (${
                            activeSelectionSet.selectedIds?.length ?? 0
                        })`}
                    </Button>
                </div>
                <div
                    className="flex items-center justify-center"
                    style={{ height: expandedHeights.manageOrAddStatements }}
                >
                    <ManageStatementsButton
                        className="text-secondary-500 font-semibold"
                        onClick={(): void => {
                            sendStatementSelectionEvent({
                                type: 'CANCEL_EDITING',
                                value: {
                                    id: selectionId,
                                },
                            });
                            setIsExpanded(false);
                        }}
                    >
                        + Manage / Add Statements
                    </ManageStatementsButton>
                </div>
            </DropdownWrapper>
        </div>
    );
};
